/**
 * This file is part of the Haiman.
 * @link     : https://haiman.io/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 Hangzhou Haila Network Technology Co., Ltd.
 */

import get from 'lodash/get';
import store from '@/store';
import { isPrivateRoom } from '@/utils/private-room';
import * as api from '@/api/secret';
import { SecretPost } from '@/api/types/secret';
import { SECRET, USER } from '@/store/types';
import { StoreDataCache, findCache, saveCache } from '@/store/cache';
import { updateObject } from '@/utils/util';
import { createPayment } from '@/utils/create-payment';
import { HttpResponseData } from '@/api/driver/http';
import { AUTH_STATE } from '@/config';
import router from '@/router';
import { isInWechat } from '@/utils/environment';
import { StoreActionEnv } from '../actions';

export interface StoreSecretPostState {
  locks: Record<number, boolean>;
  cache: StoreDataCache<SecretPost>[];
  maxCache: number;
}

export default {
  namespaced: true,
  state: {
    locks: {},
    cache: [],
    maxCache: 20,
  },
  actions: {
    [SECRET.POST_GET](
      { commit, state, rootState }: StoreActionEnv<StoreSecretPostState>,
      { id: rawId, withReport },
    ) {
      const uid = store.state.user.user && store.state.user.user.id;
      const revision = store.state.secret.privateRooms.revision;
      const id = parseInt(rawId, 10);
      if (!id) {
        throw new Error('post id must be numberic!');
      }
      const async = !!findCache(state.cache, id);
      const promise = new Promise((resolve, reject) => {
        Promise.all([
          new Promise<HttpResponseData<SecretPost>>((rsv, rej) => {
            commit(SECRET.POST_GET, { status: 'start', id });
            api.getPost(id, withReport, async).then(rsv).catch(rej);
          }),
          store.dispatch(`secret/${SECRET.GET_EMOTIONS}`),
          // store.dispatch(`secret/topic/list/${SECRET.GET_TOPIC_LIST}`),
        ]).then(([res]) => {
          if (uid !== (store.state.user.user && store.state.user.user.id) || revision !== store.state.secret.privateRooms.revision) {
            throw new Error('内容权限已变化，请刷新');
          }
          const post = res.data;
          if (post.id === 0) {
            commit(SECRET.POST_GET, { status: 'failure', id });
            store.commit(`secret/list/${SECRET.POST_UPDATE}`, { id, assign: { id: 0 } });
            reject(res);
          } else {
            commit(SECRET.POST_GET, { status: 'success', id, post });
            resolve();
          }
        }).catch((err) => {
          commit(SECRET.POST_GET, { status: 'failure', id });
          if (get(err, 'response.errcode') === 404) {
            store.commit(`secret/list/${SECRET.POST_UPDATE}`, { id, assign: { id: 0 } });
            if (rootState.user.status === AUTH_STATE.GUEST && isInWechat()) {
              const redirect = { name: 'user_login', query: { redirect: rootState.common.route.to?.fullPath } };
              router.push(redirect);
              resolve();
            }
          }
          reject(err);
        });
      });
      return promise;
    },
    async [SECRET.POST_NEW_REQUEST](_, { body, privateRoomId }) {
      if (privateRoomId && Number(body.roomId) !== Number(privateRoomId)) throw new Error('私版目标已变化，请重新进入');
      if (body.roomId) {
        const target = (await api.getRoom(body.roomId)).data;
        if (isPrivateRoom(target)) {
          if (Number(privateRoomId) !== Number(body.roomId)) throw new Error('私版发帖请从私版内进入');
          const rights = await store.dispatch('secret/privateRooms/detail', { id: body.roomId });
          if (!rights.canPost) throw new Error('当前没有私版发帖权限');
          body = { ...body, isFamous: true };
        } else if (privateRoomId) {
          throw new Error('私版目标已变化，请重新进入');
        }
      }

      return new Promise((resolve, reject) => {
        createPayment(() => api.newPost(body, { errcodeExpected: [445, 446] })).then((res) => {
          if (body.numReward) {
            store.dispatch(`user/${USER.GET}`, { action: 'refresh' });
          }
          // if (body.game) {
          //   store.dispatch(`user/profile/${USER.GET_GAME_OPTIONS}`, { id: body.game, scene: 'start', action: 'refresh' });
          // }
          store.commit(`user/${USER.UPDATE}`, { assign: { isDefaultFamous: body.isFamous } });
          resolve(res);
        }).catch(reject);
      });
    },
    async [SECRET.POST_CHANGE_RELATIONS]({ commit }, { id, filter, body }) {
      if (body.room !== void 0) {
        const [source, target] = await Promise.all([api.getPost(id, false, false), api.getRoom(body.room)]);
        const sourceRoom = source.data.room ? (await api.getRoom(source.data.room.id)).data : void 0;
        if (isPrivateRoom(sourceRoom) || isPrivateRoom(target.data)) throw new Error('不能将帖子移入或移出私版');
      }
      return new Promise((resolve, reject) => {
        api.changePostRelations(id, body).then((res) => {
          if (body.report || body.dislike || body.blocka) {
            if (body.report !== void 0) {
              const updateInfo = {
                id,
                assign: {
                  isReported: true,
                },
              };
              commit(SECRET.POST_UPDATE, updateInfo);
            }
            store.commit(`secret/list/${SECRET.POST_UPDATE}`, { id, assign: { id: 0 } });
          }
          if (body.praise !== void 0) {
            const updateInfo = {
              id,
              assign: {
                numPraise: res.data.num,
                isPraised: body.praise,
              },
            };
            commit(SECRET.POST_UPDATE, updateInfo);
          }
          if (body.approve2 !== void 0) {
            const updateInfo = {
              id,
              assign: {
                inviteInfo: null,
              },
            };
            commit(SECRET.POST_UPDATE, updateInfo);
          }
          if (body.approve !== void 0) {
            if (filter) {
              store.commit(`secret/nav/${SECRET.NAV_BADGE_UPDATE}`, {
                type: 'posts',
                subType: filter,
                offset: -1,
              });
              store.dispatch(`secret/filter/info/${SECRET.GET_FILTER_INFO}`, { action: 'refresh', filter });
            }
          }
          if (body.room !== void 0) {
            if (filter) {
              store.commit(`secret/list/${SECRET.POST_UPDATE}`, { id, assign: { id: 0 } });
              store.commit(`secret/nav/${SECRET.NAV_BADGE_UPDATE}`, {
                type: 'posts',
                subType: filter,
                offset: -1,
              });
            }
          }
          if (body.point !== void 0) {
            store.dispatch(`secret/comment/post/${SECRET.GET_COMMENT_POST}`, {
              id,
              action: 'more',
              force: true,
            });
            const updateInfo = {
              id,
              assign: {
                numPoint: res.data.point,
              },
            };
            commit(SECRET.POST_UPDATE, updateInfo);
            store.dispatch(`secret/post/${SECRET.POST_GET}`, { id });
          }
          if (body.favorite !== void 0) {
            const updateInfo = {
              id,
              assign: {
                isFavorited: body.favorite,
              },
            };
            commit(SECRET.POST_UPDATE, updateInfo);
          }
          if (body.survey !== void 0) {
            store.dispatch(`secret/post/${SECRET.POST_GET}`, { id });
          }
          resolve();
        }).catch(reject);
      });
    },
    [SECRET.POST_DEL_RELATIONS](_, { id }) {
      return new Promise((resolve, reject) => {
        api.deletePostRelations(id).then(() => {
          store.commit(`secret/list/${SECRET.POST_UPDATE}`, {
            id,
            assign: { id: 0 },
          });
          resolve();
        }).catch(reject);
      });
    },
    [SECRET.POST_DEL]({ commit }, { id, extra, type }) {
      if (id) {
        commit(SECRET.POST_DEL, { status: 'start' });
        return new Promise((resolve, reject) => {
          api.delPost(id, extra, type).then(() => {
            commit(SECRET.POST_DEL, { status: 'success', id });
            store.commit(`secret/list/${SECRET.POST_UPDATE}`, {
              id,
              assign: { id: 0 },
            });
            resolve();
          }).catch((err) => {
            commit(SECRET.POST_DEL, { status: 'failure' });
            reject(err);
          });
        });
      }
      return Promise.resolve();
    },
  },
  mutations: {
    [SECRET.POST_UPDATE](state, updateInfo) {
      const post = findCache(state.cache, updateInfo.id);
      if (post) {
        updateObject(post, {
          assign: updateInfo.assign,
          offset: updateInfo.offset,
        });
      }
      store.commit(`secret/list/${SECRET.POST_UPDATE}`, updateInfo);
    },
    [SECRET.POST_GET](state, { status, id, post }) {
      if (status === 'start') {
        state.locks[id] = true;
      } else {
        if (status === 'success') {
          store.commit(`secret/list/${SECRET.POST_UPDATE}`, {
            id: post.id,
            assign: {
              numClick: post.numClick,
              numPoint: post.numPoint,
              numPraise: post.numPraise,
              numReport: post.numReport,
              numReward: post.numReward,
              numComment: post.numComment,
              isCommented: post.isCommented,
              isViewed: post.isViewed,
            },
          });
          saveCache(state.cache, post.id, post, state.maxCache);
        }
        state.locks[id] = false;
      }
    },
    [SECRET.POST_DEL]() {},
  },
};
